/********************************************************************************
Filename: np-input-textarea.js
Created: 13 May 2021
Author: Ron Robertson
Description: A form textarea input with validation
Usage Example:
<np-input-textarea></np-input-textarea>
********************************************************************************/

(function() {
    const template = document.createElement('template');

    template.innerHTML = `
        <style>
            #npInputTextarea{
                position:relative;
                height:100%;
            }
            #labelWrapper{
                display:flex;
                flex-direction:column;
                height:100%;
            }
            .required-input{

            }
            #labelContent{
                display:inline-block;
                clear:both;
                line-height:24px;
                color: var(--labelColor, rgba(0,0,0,0.6));
            }
            #inputElement{
                box-sizing:border-box;
                border-radius:3px;
                border:1px solid #a8a8a8;
                display:block;
                box-shadow: inset 0px 1px 3px 0px rgba(0, 0, 0, 0.27);
                color:rgba(0, 0, 0, 0.95);
                padding:9px;
                min-height:36px;
                width:100%;
                height:100%;
                margin:5px 0 25px;
            }
            #inputElement.error{
                background-image: var(--formErrorIcon);
                background-repeat:no-repeat;
                background-position:right 7px top 7px;
            }

            label.error{
                background: #a60000;
                color: #ffffff;
                position: absolute;
                padding: 3px 12px 3px;
                font-size: 13px;
                border-radius: 3px;
                top: -2px;
                right: 5px;
                width: auto;
                display: none;
                float:none;
            }
            label.error::after {
                content: var(--formErrorIconDown);
                background: transparent;
                display: inline;
                position: absolute;
                top: 17px;
                right: 0px;
            }
            .fadeErrorIn{
                animation-name: fadeIn;
                animation-duration: 1s;
                animation-fill-mode: forwards;
            }
            .fadeErrorOut{
                animation-name: fadeOut;
                animation-duration: 1s;
                animation-fill-mode: forwards;
            }


            @keyframes fadeOut {
                0% {opacity: 1;}
                75% {opacity: 1;}
                100% {opacity: 0;}
            }
            @keyframes fadeIn {
                0% {opacity: 0;}
                100% {opacity: 1;}
            }

        </style>

        <div id="npInputTextarea">
            <label id="labelWrapper">
                <label id="errorLabel" class='error'></label>
                <span id="labelContent"></span>
                <textarea id="inputElement"></textarea>
            </label>
        </div><!--END npInputTextarea-->
    `;

    class NpInputTextarea extends HTMLElement {
        constructor() {
            // Always call super first in constructor
            super();

            // bind 'this' to class in the method
            this.inputChange = this.inputChange.bind(this);
            this.inputBlur = this.inputBlur.bind(this);
            this.inputFocus = this.inputFocus.bind(this);
            this.validateInput = this.validateInput.bind(this);
            this.showErrorLabel = this.showErrorLabel.bind(this);
            this.hideErrorLabel = this.hideErrorLabel.bind(this);

            // Use the Shadow DOM
            this.attachShadow({ mode: 'open' });
            this.shadowRoot.appendChild(template.content.cloneNode(true));

            // Reference to DOM Elements
            this.npInputTextarea = this.shadowRoot.getElementById("npInputTextarea");
            this.inputElement = this.shadowRoot.getElementById("inputElement");
            this.labelWrapper = this.shadowRoot.getElementById("labelWrapper");
            this.labelContent = this.shadowRoot.getElementById("labelContent");
            this.errorLabel = this.shadowRoot.getElementById("errorLabel");

            //tracks if the input is currently in focus
            this.focused = 'false';

            //tracks if the input has been validated
            this.validated = 'false';

        } // END constructor

        /* Called when added to DOM */
        connectedCallback() {
            // Set default value attribute
            if (!this.hasAttribute('value')) {
                this.setAttribute('value', "");
            }
            // Set default label attribute
            if (!this.hasAttribute('label')) {
                this.setAttribute('label', '');
            }
            // Set default validator attribute
            if (!this.hasAttribute('validator')) {
                this.setAttribute('validator', 'notempty');
            }
            // Set default tab attribute
            if (!this.hasAttribute('tab')) {
                this.setAttribute('tab', '');
            }
            // Set default name attribute
            if (!this.hasAttribute('name')) {
                this.setAttribute('name', '');
            }
            // Set default maxlength attribute
            if (!this.hasAttribute('maxlength')) {
                this.setAttribute('maxlength', '');
            }

            // set default touched to false
            this.setAttribute('touched','false');

            /* Set Event Listeners */
            this.inputElement.addEventListener('change', this.inputChange);
            this.inputElement.addEventListener('blur', this.inputBlur);
            this.inputElement.addEventListener('focus', this.inputFocus);

        } /* END connectedCallback */

        /* Listen for when attributes change */
        /* attributes must be lowercase */
        static get observedAttributes() {
            return ['value','required','validator','label','tab','name','maxlength','focused','touched','validated'];
        }

      /* Called when attributes change */
      attributeChangedCallback(name, oldValue, newValue) {

        if(name === 'value') {
            this.inputElement.value = newValue;
            this.inputElement.setAttribute('value',newValue);
        }

        if(name === 'required') {
            if(newValue == true){
                this.inputElement.classList.add('required-input');
            }else{
                this.inputElement.classList.remove('required-input');
            }
        }
        if(name === 'label') {
            this.labelContent.innerHTML=newValue;
        }
        if(name === 'tab') {
            this.inputElement.setAttribute('tabindex', newValue);
        }
        if(name === 'name') {
            this.inputElement.setAttribute('name', newValue);
        }
        if(name === 'maxlength') {
            this.inputElement.setAttribute('maxlength', newValue);
        }
        if(name === 'touched') {
            if(newValue === 'true' && this.hasAttribute('required')){
                this.validateInput();
            }
        }
       }/* END attributesChangedCallback */

        /* Getting attributes */
        get value() {
            return this.getAttribute('value');
        }

        /* Settinng attributes */
        set value(newValue) {
            this.setAttribute('value', newValue);
        }

        //get required attribute
        get required() {
            return this.hasAttribute('required');
        }
        //set required attribute
        set required(newValue){
            if(newValue == false){
                this.removeAttribute('required');
            }else{
                this.setAttribute('required','');
            }
        }

        //get validator attribute
        get validator() {
        return this.getAttribute('validator');
        }
        //set validator attribute
        set validator(newValue){
            this.setAttribute('validator', newValue);
        }

        //get label attribute
        get label() {
            return this.getAttribute('label');
        }
        //set label attribute
        set label(newValue){
            this.setAttribute('label', newValue);
        }

        //get tab attribute
        get tab() {
            return this.getAttribute('tab');
        }
        //set tab attribute
        set tab(newValue){
            this.setAttribute('tab', newValue);
        }

        //get name attribute
        get name() {
            return this.getAttribute('name');
        }
        //set name attribute
        set name(newValue){
            this.setAttribute('name', newValue);
        }

        //get maxlength attribute
        get maxlength() {
            return this.getAttribute('maxlength');
        }
        //set maxlength attribute
        set maxlength(newValue){
            this.setAttribute('maxlength', newValue);
        }

        //get touched attribute
        get touched() {
            return this.getAttribute('touched');
        }
        //set touched attribute
        set touched(newValue){
            this.setAttribute('touched', newValue);
        }

        //get focused attribute
        get focused() {
            return this.getAttribute('focused');
        }
        //set focused attribute
        set focused(newValue){
            this.setAttribute('focused', newValue);
        }

        //get valid attribute
        get validated() {
            return this.getAttribute('validated');
        }
        //set focused attribute
        set validated(newValue){
            this.setAttribute('validated', newValue);
        }

      /* Called when removed from DOM */
      disconnectedCallback() {

        /* Removing event listeners */
        this.inputElement.removeEventListener('change', this.inputChange);
      }

        /* Method */
        inputChange() {
            // track if input has been interacted with
            if(this.getAttribute('touched') === 'false'){
                this.setAttribute('touched','true');
            }
            this.value = this.inputElement.value;
        }
        inputFocus() {
            // track if input is in focus
            this.focused = 'true';

            // track if input has been interacted with
            if(this.getAttribute('touched') === 'false'){
                this.setAttribute('touched','true');
            }

            // validate input if required
            if(this.required == true){
               this.validateInput();
            }

        }
        inputBlur() {
            // track if input is in focus
            this.focused = 'false';

            // validate input if required
            if(this.required == true){
                this.validateInput();

                // hide any error labels
                this.hideErrorLabel();
            }
        }

        validateInput(){
            if(this.validator == 'notempty' || (this.validator =='email' && this.inputElement.value == "")){
                if(this.inputElement.value.length > 0){
                    this.inputElement.classList.add('valid');
                    this.inputElement.classList.remove('error');
                    this.validated = 'true';
                }else{

                    //don't show error on first focus
                   if((this.inputElement.classList.contains('valid')) || (this.inputElement.classList.contains('error') || (this.focused == 'false'))){
                        this.inputElement.classList.add('error');
                        this.inputElement.classList.remove('valid');
                        this.inputElement.classList.remove('has_info');
                        this.validated = 'false';

                        //display error label
                        this.showErrorLabel('Required field');
                   }
                }
            }else if(this.validator == 'email'){
                const inputEmail = this.inputElement.value;

                //regular expression to match email address
                const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

                //check if email matches the regular expression
                if(regex.test(String(inputEmail).toLowerCase())){
                    this.inputElement.classList.add('valid');
                    this.inputElement.classList.remove('error');
                    this.validated = 'true';
                }else{

                    //don't show error on first focus
                    if((this.inputElement.classList.contains('valid')) || (this.inputElement.classList.contains('error') || (this.focused == 'false'))){
                        this.inputElement.classList.add('error');
                        this.inputElement.classList.remove('valid');
                        this.inputElement.classList.remove('has_info');
                        this.validated = 'false';

                        //display error label
                        this.showErrorLabel('Invalid address');
                    }
                }
            }
        }
        showErrorLabel(error){
            this.errorLabel.innerHTML="";
            this.errorLabel.style.opacity = 0;
            this.errorLabel.classList.remove('fadeErrorOut');
            this.errorLabel.classList.remove('fadeErrorIn');
            this.errorLabel.style.display = 'inline';

            //update and display error label
            this.errorLabel.innerHTML = error;
            this.errorLabel.classList.add('fadeErrorIn');

        }
        hideErrorLabel(){
            //fade the error label
            this.errorLabel.classList.add('fadeErrorOut');
        }

    } /* END class */

    window.customElements.define('np-input-textarea', NpInputTextarea);

  })();
